import { cx } from "@emotion/css";
import { Plus } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import routing from "routing";
import { PrimaryButton } from "src/components/Default/LekUI/Button";
import type { FolderItemType } from "src/components/Folders/types";
import { FolderItemSkeleton } from "src/components/NewComponents/Skeletons/FolderItemSkeleton";
import CourseView from "src/components/NewComponents/Views/ScoreViews/CourseView";
import { useGetCourses } from "src/hooks/useCourses";

interface ModalSelectedItem {
	itemId: string;
	itemType: FolderItemType;
}

interface CourseItem {
	id: string;
	name?: string;
	[key: string]: unknown;
}

interface CoursesFolderTabProps {
	debouncedSearchValue: string;
	selectedItems: ModalSelectedItem[];
	toggleSelection: (item: CourseItem, type: FolderItemType) => void;
	onClose: () => void;
	setIsSearching: (value: boolean) => void;
	folderId: string;
}

export const CoursesFolderTab = ({
	debouncedSearchValue,
	selectedItems,
	toggleSelection,
	onClose,
	setIsSearching,
	folderId,
}: CoursesFolderTabProps) => {
	const { t } = useTranslation();
	const history = useHistory();

	const handleCreate = () => {
		onClose();
		history.push(`${routing.paths.pickCourseType}?folderId=${folderId}`);
	};

	const {
		data: coursesData,
		isFetching: isFetchingCoursesData,
		isLoading: isFetchingCourses,
	} = useGetCourses();

	useEffect(() => {
		setIsSearching(isFetchingCoursesData && !isFetchingCourses);
	}, [isFetchingCoursesData, isFetchingCourses, setIsSearching]);

	const courses = useMemo(() => {
		if (!coursesData) return [];
		if (!debouncedSearchValue) return coursesData;
		const lowerSearch = debouncedSearchValue.toLowerCase();
		return coursesData.filter((c: CourseItem) =>
			c.name?.toLowerCase().includes(lowerSearch),
		);
	}, [coursesData, debouncedSearchValue]);

	if (isFetchingCourses) {
		return (
			<div className="folder-modal-scroll">
				<div className="folder-modal-grid">
					{Array.from({ length: 4 }).map((_, i) => (
						<FolderItemSkeleton key={i} />
					))}
				</div>
			</div>
		);
	}

	if (!courses.length) {
		return (
			<div className="flex flex-col items-center justify-center py-48 gap-16">
				<span className="text-black-60 font-14">
					{debouncedSearchValue
						? t("folder.noSearchResults", "Brak wyników wyszukiwania")
						: t("folder.noCourses", "Nie masz jeszcze żadnych kursów")}
				</span>
				{!debouncedSearchValue && (
					<PrimaryButton
						onClick={handleCreate}
						className="flex items-center gap-8"
					>
						<Plus size={16} />
						{t("folder.createCourse", "Utwórz kurs")}
					</PrimaryButton>
				)}
			</div>
		);
	}

	return (
		<div className="folder-modal-scroll">
			<div className="folder-modal-grid">
				{courses.map((item: CourseItem) => {
					const isSelected = selectedItems.some(
						(i) => i.itemId === item.id && i.itemType === "course",
					);

					return (
						<div
							key={item.id as string}
							data-test-id="folder-modal-course-item"
							data-course-id={item.id}
							onClick={() => toggleSelection(item, "course")}
							className={cx("cursor-pointer position-relative transition-all")}
						>
							<div
								className={cx(
									"pointer-events-none border-2 border-solid border-radius-16 radius-16",
								)}
							>
								<CourseView
									hideButton
									row={item}
									className={cx(
										"mb-0 w-100 mx-0",
										isSelected ? "border-color-primary" : "",
									)}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
